import type {
  AssortmentTypeDetailDto,
  AssortmentTypeDto,
  UpsertAssortmentTypeRequest,
} from '@/types/AssortmentType'
import {
  ApiClient,
  ApiError,
  apiDelete,
  apiGet,
  apiGetWithPagedResult,
  apiPut,
  type PagedResult,
  type WithEtag,
} from './ApiClient'

const assortmentTypesPath = '/assortment-types'
const assortmentTypesPageSize = 100

const attachEtag = <T extends object>(data: T, etag: string | null): WithEtag<T> => {
  Object.defineProperty(data, 'etag', {
    value: etag,
    enumerable: false,
    writable: false,
    configurable: false,
  })

  return data as WithEtag<T>
}

export async function getAssortmentTypes(): Promise<AssortmentTypeDto[]> {
  const items: AssortmentTypeDto[] = []
  let continuationToken: string | null = null
  let hasMoreResults = true

  do {
    const pagedResult: PagedResult<AssortmentTypeDto> =
      await apiGetWithPagedResult<AssortmentTypeDto>(assortmentTypesPath, {
        pageSize: assortmentTypesPageSize,
        continuationToken,
      })

    items.push(...pagedResult.items)
    continuationToken = pagedResult.continuationToken
    hasMoreResults = pagedResult.hasMoreResults
  } while (hasMoreResults)

  return items
}

export async function getAssortmentType(id: string): Promise<WithEtag<AssortmentTypeDetailDto>> {
  return apiGet<AssortmentTypeDetailDto>(
    `${assortmentTypesPath}/${encodeURIComponent(id)}`,
  ) as Promise<WithEtag<AssortmentTypeDetailDto>>
}

export async function createAssortmentType(
  body: UpsertAssortmentTypeRequest,
): Promise<WithEtag<AssortmentTypeDto>> {
  const response = await ApiClient.post<AssortmentTypeDto>(assortmentTypesPath, body)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return attachEtag(response.data, response.headers.get('etag'))
}

export async function updateAssortmentType(
  id: string,
  body: UpsertAssortmentTypeRequest,
  etag: string,
): Promise<WithEtag<AssortmentTypeDto>> {
  return apiPut<AssortmentTypeDto>(`${assortmentTypesPath}/${encodeURIComponent(id)}`, body, {
    etag,
  }) as Promise<WithEtag<AssortmentTypeDto>>
}

export async function deleteAssortmentType(id: string, etag: string): Promise<void> {
  await apiDelete<void>(`${assortmentTypesPath}/${encodeURIComponent(id)}`, {
    headers: {
      'If-Match': etag,
    },
  })
}
